import React from "react";
import { Dropdown } from "semantic-ui-react";
import styled from "styled-components";

import { useTooltip } from "../Tooltip/Tooltip";
import { isWrite } from "src/modules/User";
import { SubmissionStatus } from "src/types/Submission";
import { getOptionsForCurrentStatus } from "./StatusChangeDropdown";
import { Status, getColorFromStatus, getTextFromStatus } from "./StatusBadge";

interface SubmissionStatusDropdownProps {
  status: string;
  submissionId: number;
  pwsName: string;
  submissionPeriodId: number;
  openChangeSubmissionStatus: (
    value: string,
    pwsId: number,
    submissionPeriodId: number,
    pwsName: string,
    submissionId: number,
    refetch: () => Promise<any>
  ) => void;
  refetch: () => Promise<any>;
  pwsId: number;
  tooltipText?: string;
}

const STATIC_STATUSES: Status[] = ["processingSubmission", "failed", "rejected", "voided"];

const SubmissionStatusDropdown: React.FC<SubmissionStatusDropdownProps> = ({
  status,
  submissionId,
  pwsName,
  submissionPeriodId,
  openChangeSubmissionStatus,
  refetch,
  pwsId,
  tooltipText = "Change the Submission Status",
}) => {
  const resolvedStatus: Status = (status || "notSubmitted") as Status;

  const { setTooltipContent, setTooltipPosition } = useTooltip();

  const isStaticStatus = STATIC_STATUSES.includes(resolvedStatus);

  if (!isWrite() || isStaticStatus || !submissionId) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <StaticBadge
          $bgColor={getColorFromStatus(resolvedStatus)}
          $textColor={getTextFromStatus(resolvedStatus)}
        >
          {SubmissionStatus[resolvedStatus] ?? resolvedStatus}
        </StaticBadge>
      </div>
    );
  }

  const statusOptions = getOptionsForCurrentStatus();

  const handleChange = (
    _: React.SyntheticEvent<HTMLElement>,
    { value }: { value: string }
  ): void => {
    openChangeSubmissionStatus(
      value,
      pwsId,
      submissionPeriodId,
      pwsName,
      submissionId,
      refetch
    );
  };

  return (
    <StyledDropdown
      aria-label="Change submission status"
      onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
      onMouseDown={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
      onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition(rect.x + rect.width, rect.y + rect.height - 90);
        setTooltipContent(<span>{tooltipText}</span>);
      }}
      onMouseLeave={() => setTooltipContent(null)}
      value={resolvedStatus}
      options={statusOptions}
      onChange={handleChange}
      selection
    />
  );
};

const BADGE_WIDTH = "145px";
const BADGE_HEIGHT = "24px";

const StyledDropdown = styled(Dropdown)<{ value: string }>`
    &.ui.dropdown {
    width: ${BADGE_WIDTH} !important;
    height: ${BADGE_HEIGHT} !important;
    min-width: unset !important;
    min-height: unset !important;
    text-align: center;
    background-color: ${({ value }) => getColorFromStatus(value as Status)} !important;
    color: ${({ value }) => getTextFromStatus(value as Status)} !important;
    font-weight: 500;
    font-size: 13px;
    border-radius: 5px !important;
    padding: 0 8px !important;
    line-height: ${BADGE_HEIGHT} !important;
    cursor: pointer;
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    border: none !important;

    .dropdown.icon {
      top: 50% !important;
      transform: translateY(-25%);
    }
  }
`;

const StaticBadge = styled.span<{ $bgColor: string; $textColor?: string }>`
  display: inline-block;
  width: ${BADGE_WIDTH};
  height: ${BADGE_HEIGHT};
  color: ${({ $textColor }) => $textColor ?? "black"};
  font-weight: 500;
  font-size: 13px;
  border-radius: 5px;
  padding: 0 8px;
  line-height: ${BADGE_HEIGHT};
  text-align: center;
  background-color: ${({ $bgColor }) => $bgColor};
  box-sizing: border-box;
`;

export default SubmissionStatusDropdown;
